import React, {useState, useCallback, useEffect} from 'react';
import {AdminAccessContext} from './AdminAccessContext';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useAuthContext} from 'packages/common/hooks';
import {roles} from 'packages/common/utils/rolesConfig';
import {ADMIN_ACCESS_STATE_KEY} from 'packages/common/utils/sessionStorageKeys';
import {paths} from 'packages/common/routes/paths';

// L'accès client (impersonation) est réservé au super admin
const ADMIN_ROLES = [roles.APP_ADMIN];

/** État d'un opérateur qui n'est engagé dans aucun accès client. */
const CLEARED_ADMIN_ACCESS = Object.freeze({
  isAdminAccessMode: false,
  targetCompany: null,
  remainingTime: null,
  reason: null,
  isTransitioning: false,
});

export const AdminAccessProvider = ({children}) => {
  const {userRole} = useAuthContext();

  // Initialiser avec localStorage mais marquer comme en transition pour valider avec le backend
  const [adminAccessState, setAdminAccessState] = useState(() => {
    try {
      const stored = localStorage.getItem(ADMIN_ACCESS_STATE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Si état admin présent, marquer en transition jusqu'à validation backend
        if (parsed.isAdminAccessMode) {
          return {...parsed, isTransitioning: true};
        }
        return parsed;
      }
    } catch (error) {
      // Ignore parse errors
    }
    return CLEARED_ADMIN_ACCESS;
  });

  /**
   * Referme l'accès client et ramène l'opérateur à son panel.
   *
   * Le rechargement complet est voulu : les données de l'établissement visité
   * restent en cache SWR et dans les contextes déjà montés, une navigation
   * interne les laisserait affichées sous une session qui ne les autorise plus.
   */
  const returnToAdminPanel = useCallback(() => {
    localStorage.removeItem(ADMIN_ACCESS_STATE_KEY);
    setAdminAccessState(CLEARED_ADMIN_ACCESS);
    window.location.href = paths.admin.root;
  }, []);

  const checkStatus = useCallback(async () => {
    // Si l'utilisateur n'est pas admin, nettoyer tout état admin résiduel
    if (!ADMIN_ROLES.includes(userRole)) {
      if (adminAccessState.isAdminAccessMode) {
        localStorage.removeItem(ADMIN_ACCESS_STATE_KEY);
        setAdminAccessState(CLEARED_ADMIN_ACCESS);
      }
      return;
    }

    try {
      const response = await axiosInstance.get(endpoints.admin.access.status);

      if (response.data.success && response.data.data.adminAccessMode) {
        const newState = {
          isAdminAccessMode: true,
          targetCompany: {
            id: response.data.data.targetCompanyId,
            name: response.data.data.targetCompanyName,
          },
          remainingTime: response.data.data.remainingTime,
          reason: response.data.data.reason,
          isTransitioning: false,
        };
        localStorage.setItem(ADMIN_ACCESS_STATE_KEY, JSON.stringify(newState));
        setAdminAccessState(newState);
      } else if (adminAccessState.isAdminAccessMode) {
        // Le backend ne reconnaît plus d'accès client : la fenêtre est échue ou
        // l'accès a été révoqué. On ramène l'opérateur à son panel au lieu de le
        // laisser sur un tableau de bord dont chaque appel répond désormais 403.
        returnToAdminPanel();
      } else {
        localStorage.removeItem(ADMIN_ACCESS_STATE_KEY);
        setAdminAccessState(CLEARED_ADMIN_ACCESS);
      }
    } catch (error) {
      // Silently fail - no need to log in production
    }
  }, [userRole, adminAccessState.isAdminAccessMode, returnToAdminPanel]);

  const grantAccess = useCallback(async (targetCompanyId, reason, targetUserId = null) => {
    try {
      // Activer l'état de transition pour masquer la barre pendant la redirection
      setAdminAccessState((prev) => ({...prev, isTransitioning: true}));

      const response = await axiosInstance.post(endpoints.admin.access.grant, {
        targetCompanyId,
        reason,
        targetUserId,
      });

      if (response.data.success) {
        const newState = {
          isAdminAccessMode: true,
          targetCompany: response.data.data.targetCompany,
          remainingTime: response.data.data.expiresIn,
          reason: response.data.data.reason,
          isTransitioning: true, // Garder la transition active jusqu'à la redirection
        };
        localStorage.setItem(ADMIN_ACCESS_STATE_KEY, JSON.stringify(newState));
        setAdminAccessState(newState);

        return {success: true, data: response.data.data};
      }

      // Désactiver la transition en cas d'erreur
      setAdminAccessState((prev) => ({...prev, isTransitioning: false}));
      return {success: false, error: response.data.message || 'Erreur lors de l\'accès au compte'};
    } catch (error) {
      setAdminAccessState((prev) => ({...prev, isTransitioning: false}));
      return {success: false, error: error.response?.data?.message || error.message};
    }
  }, []);

  const revokeAccess = useCallback(async (actionsPerformed = []) => {
    try {
      const response = await axiosInstance.post(endpoints.admin.access.revoke, {
        actionsPerformed,
      });

      // Toujours nettoyer l'état local, que le backend réussisse ou non
      localStorage.removeItem(ADMIN_ACCESS_STATE_KEY);
      setAdminAccessState(CLEARED_ADMIN_ACCESS);

      if (response.data.success) {
        return {success: true};
      }

      // Même en cas d'échec backend, on considère le revoke comme réussi localement
      // Car l'état local était probablement périmé
      return {success: true, warning: response.data.message || 'Session expirée côté serveur'};
    } catch (error) {
      // En cas d'erreur réseau ou autre, nettoyer quand même l'état local
      // pour éviter de bloquer l'utilisateur avec un état périmé
      localStorage.removeItem(ADMIN_ACCESS_STATE_KEY);
      setAdminAccessState(CLEARED_ADMIN_ACCESS);
      return {success: true, warning: error.response?.data?.message || 'Session nettoyée localement'};
    }
  }, []);

  // Vérification initiale et périodique du statut admin
  useEffect(() => {
    // Toujours vérifier au montage pour nettoyer tout état résiduel (même pour non-admins)
    checkStatus();

    // Pour les admins, vérifier périodiquement
    if (ADMIN_ROLES.includes(userRole)) {
      // Vérifier plus fréquemment si on approche de l'expiration (< 2 min)
      const intervalDelay = adminAccessState.remainingTime && adminAccessState.remainingTime < 120 ? 10000 : 30000;

      const interval = setInterval(checkStatus, intervalDelay);
      return () => clearInterval(interval);
    }
  }, [checkStatus, userRole, adminAccessState.remainingTime]);

  // Le décompte annoncé par le backend est arrivé à zéro entre deux vérifications
  useEffect(() => {
    if (adminAccessState.isAdminAccessMode && adminAccessState.remainingTime !== null && adminAccessState.remainingTime <= 0) {
      returnToAdminPanel();
    }
  }, [adminAccessState.isAdminAccessMode, adminAccessState.remainingTime, returnToAdminPanel]);

  return (
    <AdminAccessContext.Provider
      value={{
        ...adminAccessState,
        grantAccess,
        revokeAccess,
        checkStatus,
      }}
    >
      {children}
    </AdminAccessContext.Provider>
  );
};
